/**
 * Where an employee punched out, judged against where the day was worked.
 *
 * The register used to show only the punch-in's place, so a day that started
 * at the office and ended kilometres away read as a clean office day unless
 * somebody opened the map. Rows recorded since the punch-out kept its own
 * verdict (`punch_out_geofence_result`) use that; older rows are measured here
 * against the same office or site the punch-in was.
 *
 * A day whose last place was a site (moved out with "At a site now", or
 * punched in from one) is expected to end wherever the site is.
 */

const toRad = (deg) => (deg * Math.PI) / 180;

function metresBetween(lat1, lng1, lat2, lng2) {
  const [a, b, c, d] = [lat1, lng1, lat2, lng2].map(Number);
  if (![a, b, c, d].every(Number.isFinite)) return null;
  const h =
    Math.sin(toRad(c - a) / 2) ** 2 +
    Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(toRad(d - b) / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The place they were at when they punched out: their last move, or the punch-in's mode. */
function modeAtPunchOut(row) {
  const out = row.check_out ? new Date(row.check_out).getTime() : Infinity;
  const moves = (Array.isArray(row.moves) ? row.moves : [])
    .filter((m) => new Date(m.moved_at).getTime() <= out)
    .sort((x, y) => new Date(x.moved_at) - new Date(y.moved_at));
  return moves.length ? moves[moves.length - 1].mode : row.punch_mode;
}

/**
 * { kind: 'away', distance_m, name } | { kind: 'office', name } | { kind: 'site' } | { kind: 'wfh' }
 * | null when there is no app punch-out to judge, or nothing to judge it against.
 *
 * `fence` is the office or site the punch-in was measured against
 * ({ lat, lng, radius_m, name }), or null.
 */
export function punchOutPlace(row, fence) {
  if (!row?.check_out || row.punch_out_lat == null) return null;
  const mode = modeAtPunchOut(row);
  if (mode === 'SITE') return { kind: 'site' };
  // A work-from-home day ends at home; there is nothing to flag.
  if (mode === 'WFH') return { kind: 'wfh' };

  const name = fence?.name ?? null;
  if (row.punch_out_geofence_result === 'OUTSIDE_ALLOWED') {
    return { kind: 'away', distance_m: row.punch_out_distance_m ?? null, name };
  }
  if (row.punch_out_geofence_result === 'INSIDE') return { kind: 'office', name };
  if (row.punch_out_geofence_result === 'UNCHECKED') return null;

  if (!fence) return null;
  const distance = metresBetween(row.punch_out_lat, row.punch_out_lng, fence.lat, fence.lng);
  if (distance == null) return null;
  // The same slack the server gives a punch: the fix's own accuracy, unless
  // the fix is too poor to say anything.
  const accuracy = Number(row.punch_out_accuracy_m);
  const slack = Number.isFinite(accuracy) && accuracy > 0 && accuracy <= 1000 ? accuracy : 0;
  const radius = Number(fence.radius_m) || 200;
  return distance > radius + slack
    ? { kind: 'away', distance_m: Math.round(distance), name }
    : { kind: 'office', name };
}

/** "350 m", "6.2 km". */
export function formatAwayDistance(metres) {
  const m = Number(metres);
  if (metres == null || !Number.isFinite(m)) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}
